import React from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import tinycolor from "tinycolor2";

const weekdayLabels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const getManilaDateKey = (date = new Date()) => date.toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });

const parseDateKey = (value) => {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
};

const formatDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const formatDateLabel = (value, placeholder) => {
  const date = parseDateKey(value);
  if (!date) return placeholder;
  return date.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getMonthLabel = (date) => date.toLocaleDateString("en-PH", { month: "long", year: "numeric" });

export default function DatePicker({
  value,
  onChange,
  themeColor = "#0f172a",
  className = "",
  placeholder = "Select date",
  ariaLabel = "Select date",
  minDate,
  maxDate,
}) {
  const pickerRef = React.useRef(null);
  const popoverRef = React.useRef(null);
  const todayKey = getManilaDateKey();
  const selectedDate = parseDateKey(value);
  const initialDate = selectedDate || parseDateKey(todayKey) || new Date();
  const [open, setOpen] = React.useState(false);
  const [popoverStyle, setPopoverStyle] = React.useState(null);
  const [viewDate, setViewDate] = React.useState(() => new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
  const activeTint = tinycolor(themeColor).setAlpha(0.1).toRgbString();
  const focusTint = tinycolor(themeColor).setAlpha(0.14).toRgbString();
  const minDateObj = parseDateKey(minDate);
  const maxDateObj = parseDateKey(maxDate);

  React.useEffect(() => {
    if (selectedDate && !open) {
      setViewDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }
  }, [open, selectedDate]);

  React.useEffect(() => {
    if (!open) return undefined;

    const updatePopoverPosition = () => {
      const triggerRect = pickerRef.current?.getBoundingClientRect();
      if (!triggerRect) return;

      const popoverWidth = Math.min(332, Math.max(292, window.innerWidth - 32));
      const left = Math.min(
        Math.max(16, triggerRect.right - popoverWidth),
        window.innerWidth - popoverWidth - 16
      );
      const top = Math.min(triggerRect.bottom + 8, window.innerHeight - 24);

      setPopoverStyle({
        position: "fixed",
        top,
        left,
        width: popoverWidth,
      });
    };

    const handlePointerDown = (event) => {
      const isInsideTrigger = pickerRef.current?.contains(event.target);
      const isInsidePopover = popoverRef.current?.contains(event.target);
      if (!isInsideTrigger && !isInsidePopover) setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    updatePopoverPosition();
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updatePopoverPosition);
    window.addEventListener("scroll", updatePopoverPosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updatePopoverPosition);
      window.removeEventListener("scroll", updatePopoverPosition, true);
    };
  }, [open]);

  const monthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const calendarStart = new Date(monthStart);
  calendarStart.setDate(monthStart.getDate() - monthStart.getDay());

  const days = Array.from({ length: 42 }, (_, index) => {
    const date = new Date(calendarStart);
    date.setDate(calendarStart.getDate() + index);
    return date;
  });

  const isDisabled = (date) => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (minDateObj && dateOnly < minDateObj) return true;
    if (maxDateObj && dateOnly > maxDateObj) return true;
    return false;
  };

  const moveMonth = (amount) => {
    setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + amount, 1));
  };

  const selectDate = (date) => {
    if (isDisabled(date)) return;
    onChange?.(formatDateKey(date));
    setOpen(false);
  };

  const selectToday = () => {
    const today = parseDateKey(todayKey);
    if (!today || isDisabled(today)) return;
    onChange?.(todayKey);
    setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setOpen(false);
  };

  const clearDate = () => {
    onChange?.("");
    setOpen(false);
  };

  return (
    <div ref={pickerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-12 w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 text-left text-sm font-semibold text-slate-900 shadow-sm outline-none transition hover:border-[var(--color-primary)] focus:border-[var(--color-primary)] focus:shadow-[0_0_0_4px_rgba(148,163,184,0.14)]"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={`truncate ${value ? "text-slate-900" : "text-slate-400"}`}>
          {formatDateLabel(value, placeholder)}
        </span>
        <CalendarDays className="h-4.5 w-4.5 shrink-0 text-slate-500" />
      </button>

      {open && typeof document !== "undefined" && createPortal((
        <div
          ref={popoverRef}
          className="z-[9999] rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_24px_60px_rgba(15,23,42,0.18)]"
          style={popoverStyle || { position: "fixed", top: -9999, left: -9999, width: 332 }}
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => moveMonth(-1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div
              className="rounded-full px-4 py-2 text-sm font-semibold text-slate-900"
              style={{ background: activeTint }}
            >
              {getMonthLabel(viewDate)}
            </div>
            <button
              type="button"
              onClick={() => moveMonth(1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
            {weekdayLabels.map((day) => <div key={day}>{day}</div>)}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map((date) => {
              const dateKey = formatDateKey(date);
              const selected = value === dateKey;
              const isToday = todayKey === dateKey;
              const outsideMonth = date.getMonth() !== viewDate.getMonth();
              const disabled = isDisabled(date);

              return (
                <button
                  key={dateKey}
                  type="button"
                  onClick={() => selectDate(date)}
                  disabled={disabled}
                  className="h-9 rounded-xl text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-35"
                  style={{
                    border: selected ? `1px solid ${themeColor}` : isToday ? `1px solid ${focusTint}` : "1px solid transparent",
                    background: selected ? themeColor : isToday ? activeTint : "transparent",
                    color: selected ? "#ffffff" : outsideMonth ? "#94a3b8" : "#0f172a",
                    boxShadow: selected ? `0 10px 24px ${focusTint}` : "none",
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between">
            <button
              type="button"
              onClick={clearDate}
              className="inline-flex items-center gap-1 rounded-xl px-3 py-2 text-xs font-semibold text-slate-500 transition hover:bg-slate-50"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
            <button
              type="button"
              onClick={selectToday}
              className="rounded-xl px-3 py-2 text-xs font-semibold transition hover:bg-slate-50"
              style={{ color: themeColor }}
            >
              Today
            </button>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
